import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const navLinks = [
  { href: '#accueil', label: 'Accueil' },
  { href: '#menu', label: 'Menu' },
  { href: '#apropos', label: 'À propos' },
  { href: '#galerie', label: 'Galerie' },
  { href: '#commander', label: 'Commander' },
  { href: '#contact', label: 'Contact' },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#accueil"
            onClick={(e) => handleNavClick(e, '#accueil')}
            className="flex items-center space-x-2"
          >
            <span className={`text-2xl font-bold transition-colors duration-300 ${
              isScrolled ? 'text-amber-700' : 'text-white'
            }`}>
              Le Paradis
            </span>
            <span className={`text-sm font-medium transition-colors duration-300 ${
              isScrolled ? 'text-amber-600' : 'text-amber-200'
            }`}>
              Bondy
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 hover:bg-amber-500/20 ${
                  isScrolled
                    ? 'text-gray-700 hover:text-amber-700'
                    : 'text-white/90 hover:text-white'
                }`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-3">
            <a href="tel:0141552585">
              <Button
                variant="ghost"
                size="sm"
                className={`transition-colors duration-300 ${
                  isScrolled
                    ? 'text-amber-700 hover:bg-amber-50'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                <Phone className="w-4 h-4 mr-2" />
                Appeler
              </Button>
            </a>
            <a href="#commander" onClick={(e) => handleNavClick(e, '#commander')}>
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Commander
              </Button>
            </a>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button
                variant="ghost"
                size="icon"
                className={isScrolled ? 'text-gray-700' : 'text-white'}
              >
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-white">
              <div className="flex flex-col h-full py-6">
                <div className="flex items-center justify-between mb-8">
                  <span className="text-2xl font-bold text-amber-700">Le Paradis</span>
                </div>
                <nav className="flex flex-col space-y-1">
                  {navLinks.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={(e) => handleNavClick(e, link.href)}
                      className="px-4 py-3 rounded-lg text-gray-700 font-medium hover:bg-amber-50 hover:text-amber-700 transition-colors duration-200"
                    >
                      {link.label}
                    </a>
                  ))}
                </nav>
                <div className="mt-auto space-y-3">
                  <a href="tel:0141552585" className="block">
                    <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white">
                      <Phone className="w-4 h-4 mr-2" />
                      01 41 55 25 85
                    </Button>
                  </a>
                  <a
                    href="https://www.google.com/maps/dir//43+Av.+du+Mar%C3%A9chal+de+Lattre+de+Tassigny,+93140+Bondy,+France"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <Button variant="outline" className="w-full border-amber-600 text-amber-700 hover:bg-amber-50">
                      <MapPin className="w-4 h-4 mr-2" />
                      Itinéraire
                    </Button>
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
